/**
 * `?focus=<field>` on any drilldown URL: once the tab renders, scroll the
 * element marked `data-drilldown-field="<field>"` into view and flash it, then
 * drop the param so a refresh / back doesn't replay it.
 *
 * Used by the Closing readiness "Go to …" links on Scheduling entries
 * (`scheduleFieldHref` in closingReadinessUtils). Tabs render lazily and
 * cards mount their bodies late, so the target is polled for briefly; if it
 * never appears (field hidden for this loan, classic view, …) the param is
 * dropped and nothing else happens.
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { teal } from "../../../theme/colors";

const POLL_MS = 100;
const MAX_TRIES = 40; // ~4s

export const useDrilldownFieldFocus = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const field = params.get("focus");
    if (!field) return undefined;

    const clearParam = () => {
      params.delete("focus");
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
        { replace: true },
      );
    };

    let tries = 0;
    let timer = null;
    const selector = `[data-drilldown-field="${CSS.escape(field)}"]`;
    const attempt = () => {
      const el = document.querySelector(selector);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.animate?.(
          [
            { backgroundColor: teal[100], boxShadow: `inset 3px 0 0 ${teal[500]}` },
            { backgroundColor: teal[100], boxShadow: `inset 3px 0 0 ${teal[500]}`, offset: 0.6 },
            { backgroundColor: "transparent", boxShadow: "inset 3px 0 0 transparent" },
          ],
          { duration: 2400, easing: "ease-out" },
        );
        clearParam();
        return;
      }
      tries += 1;
      if (tries >= MAX_TRIES) {
        clearParam();
        return;
      }
      timer = setTimeout(attempt, POLL_MS);
    };
    timer = setTimeout(attempt, POLL_MS);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search, location.hash, navigate]);
};

export default useDrilldownFieldFocus;
